import { data, Link, useLoaderData } from 'react-router'
import { Card, CardContent, CardTitle } from '#app/components/ui/card.tsx'
import { Icon } from '#app/components/ui/icon.js'
import { type SeoProps } from '#app/seo/seo.tsx'
import { buildItemListLd } from '#app/seo/structured-data.ts'
import { prisma } from '#app/utils/db.server.ts'
import { type Route } from './+types/index.ts'

export async function loader({}: Route.LoaderArgs) {
	const categories = await prisma.category.findMany({
		where: {
			businesses: {
				some: {
					status: 'published',
				},
			},
		},
		select: {
			id: true,
			name: true,
			slug: true,
			_count: {
				select: {
					businesses: {
						where: {
							status: 'published',
						},
					},
				},
			},
		},
		orderBy: {
			name: 'asc',
		},
	})

	return data({ categories })
}

// Breadcrumb handle for automatic breadcrumb generation
export const handle = {
	breadcrumb() {
		return { name: 'Categories', url: '/businesses/categories' }
	},
	seo({
		loaderData,
		origin,
		canonical,
	}: {
		loaderData: Awaited<ReturnType<typeof loader>>['data']
		origin: string
		canonical: string
	}): SeoProps {
		const structuredData =
			loaderData.categories.length > 0
				? [
						buildItemListLd({
							name: 'Business Categories',
							items: loaderData.categories.map((category) => ({
								name: category.name,
								url: `${origin}/businesses/category/${category.slug}`,
							})),
						}),
					]
				: undefined

		return {
			title: 'All Dallas - Fort Worth Business Categories - RentDFW.com',
			description:
				'Browse all business categories in the Dallas-Fort Worth area. Find the professional services you need.',
			canonical,
			openGraph: {
				title: 'All Dallas - Fort Worth Business Categories - RentDFW.com',
				description:
					'Browse all business categories in the Dallas-Fort Worth area. Find the professional services you need.',
				type: 'website',
				url: canonical,
			},
			twitter: {
				card: 'summary_large_image',
				title: 'All Dallas - Fort Worth Business Categories - RentDFW.com',
				description:
					'Browse all business categories in the Dallas-Fort Worth area. Find the professional services you need.',
			},
			structuredData,
		}
	},
}

export default function BusinessCategoriesPage() {
	const { categories } = useLoaderData<typeof loader>()

	return (
		<>
			{/* SEO is handled by SeoHead via handle.seo */}
			<div className="container mx-auto px-4 py-16">
				<div className="mx-auto max-w-3xl text-center">
					<h1 className="text-4xl font-semibold text-balance">
						All Categories
					</h1>
					<p className="text-muted-foreground mt-2 mb-8 text-xl">
						{categories.length}{' '}
						{categories.length === 1 ? 'category' : 'categories'} of
						professional services in the Dallas-Fort Worth metroplex.
					</p>
				</div>

				{categories.length === 0 ? (
					<div className="bg-muted rounded-lg py-16 text-center">
						<Icon
							name="file-text"
							className="text-muted-foreground mx-auto mb-4 size-12"
						/>
						<h3 className="mb-2 text-xl font-semibold">No Categories Yet</h3>
						<p className="text-muted-foreground">
							Categories will appear here once added.
						</p>
					</div>
				) : (
					<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
						{categories.map((category) => (
							<Link
								key={category.id}
								to={`/businesses/category/${category.slug}`}
								className="group"
							>
								<Card className="h-full transition-all group-hover:shadow-md">
									<CardContent>
										<div className="flex items-start justify-between">
											<div className="flex-1">
												<CardTitle className="group-hover:text-primary text-lg transition-colors">
													{category.name}
												</CardTitle>
											</div>
											<Icon
												name="chevron-right"
												className="text-muted-foreground size-5 transition-transform group-hover:translate-x-1"
											/>
										</div>
										<div className="text-muted-foreground flex items-center gap-2 text-sm">
											<span>
												{category._count.businesses}{' '}
												{category._count.businesses === 1
													? 'business'
													: 'businesses'}
											</span>
										</div>
									</CardContent>
								</Card>
							</Link>
						))}
					</div>
				)}
			</div>
		</>
	)
}
